import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useLocation, useParams } from 'react-router';
import CampaignHeader from '@/components/CampaignHeader/CampaignHeader';
import CampaignHistory from '@/components/CampaignHistory/CampaignHistory';
import CampaignSearchPopup from '@/components/CampaignSearchPopup/CampaignSearchPopup';
import CampaignStatus from '@/components/CampaignStatus/CampaignStatus';
import Help from '@/components/Help/Help';
import ScrollValidationIndicator from '@/components/ScrollValidationIndicator/ScrollValidationIndicator';
import CampaignHeaderSkeleton from '@/components/Skeletons/CampaignHeaderSkeleton';
import CampaignStatusSkeleton from '@/components/Skeletons/CampaignStatusSkeleton';
import SumsCriteriaPopup from '@/components/SumsCriteriaPopup/SumsCriteriaPopup';
import Spinner from '@/components/UI/Spinner/Spinner';
import { errorTypes, helpTextTabs, statusCampaign, tabsCRSearchOrdered, VAN_CAMPAIGN_MANAGER, VAN_ROLES, ROUTES, highlightedFieldsInitialState } from '@/helpers/constants';
import { getCRSearchFromCRInfo } from '@/helpers/opensearch-utils';
import { getVersionUrlParam, isUserInRecipients, scrollToField, tabLowerCase } from '@/helpers/utils';
import useApiRequest from '@/hooks/useApiRequest';
import useAppLanguage from '@/hooks/useAppLanguage';
import useAuth from '@/hooks/useAuth';
import useCampaignValidate from '@/hooks/useCampaignValidate';
import { useFieldSelection } from '@/hooks/useFieldSelection';
import useGlobalSpinnerContext from '@/hooks/useGlobalSpinner';
import useGoTo from '@/hooks/useGoTo';
import useToastContext from '@/hooks/useToastContext';
import useVisibilityMatrix from '@/hooks/useVisibilityMatrix';
import { setCampaignInfo, resetCampaign, setCampaignSearch, setTranslations, toggleCampaignHistory } from '@/redux/Campaign/campaign.action';
import { getCampaignHelp, getCampaignHistory, getCampaignInfo, getCampaignSearch, getCampaignValidations, getCampaignValidationsActive, getIsVANCR } from '@/redux/Campaign/campaign.reducer';
import { setHeaderLanguage } from '@/redux/Header/header.action';
import { setHighlightedFields } from '@/redux/HighlightFields/highlightFields.action';
import { getHelpResizer, getWFResizer } from '@/redux/User/user.reducer';
import campaignServices from '@/services/campaignServices';
import titleService from '@/services/titleService';
import { CampaignHistory as CampaignHistoryType, CampaignInfo, CampaignSearch, HelpTextState, HighlightedFields, SelectedFieldsByTab, Validations } from '@/types';
import { StatusCampaign, StatusSubCampaign, VisibilityMatrixAction, VisibilityMatrixFeature } from '@/types/Matrix';
import useQuery from '@/utils/hooks';
import AffectedVehicles from './AffectedVehicles/AffectedVehicles';
import Attachments from './Attachments/Attachments';
import Classification from './Classification/Classification';
import Description from './Description/Description';
import Hardware from './Hardware/Hardware';
import PlantFeedback from './PlantFeedback/PlantFeedback';
import Recipients from './Recipients/Recipients';
import Software from './Software/Software';
import Summary from './Summary/Summary';
import Translations from './Translations/Translations';
import VehicleStatusOverview from './VehicleStatusOverview/VehicleStatusOverview';
import styles from './CampaignDetail.module.scss';

interface CampaignDetailProps {
  campaignInfo: CampaignInfo;
  campaignHelp: HelpTextState;
  campaignHistory: CampaignHistoryType;
  setInfo: (info: CampaignInfo | { id: string }, fullReset: boolean) => void;
  resizerWF: number;
  resizerHelp: number;
  readOnly: boolean;
  setHeaderLang: (lang: string) => void;
  resetCR: () => void;
  validationsActivated: { [tab: string]: boolean };
  setCRSearch: (search: CampaignSearch) => void;
  campaignSearch: CampaignSearch;
  validations: Validations;
  setCampaignTranslations: (translations: object) => void;
  isVANCR: boolean;
  toggleHistory: (show: boolean) => void;
  setSelectedFields: (highlightedFields: HighlightedFields) => void;
}

const CampaignDetail: React.FC<CampaignDetailProps> = ({ campaignInfo, campaignHelp, campaignHistory, setInfo, resizerWF,
  resizerHelp, readOnly, setHeaderLang, resetCR, validationsActivated, setCRSearch, campaignSearch, validations,
  isVANCR, toggleHistory, setSelectedFields }) => {
  const { id, tab, version = '' } = useParams<{ id: string; tab: string; version: string }>();
  const [searchCR, showSUMSPopup] = [useQuery().get('search'), useQuery().get('showSUMSPopup')];
  const { t } = useTranslation();
  const lang = useAppLanguage();
  const isSummary = window.location.pathname.toLowerCase().includes('/summary');
  const [isValid, setIsValid] = useState(!isSummary);
  const [isSubCRUserAssigned, setIsSubCRUserAssigned] = useState(false);
  const isLoading = !campaignInfo?.id || !isValid || (searchCR && !campaignSearch?.searchedText);
  const { addToast } = useToastContext();
  const { canSee } = useVisibilityMatrix();
  const authService = useAuth();
  const canSeeCR = (action: VisibilityMatrixAction) => canSee({
    feature: VisibilityMatrixFeature.CAMPAIGN_REQUEST,
    action: action,
    status: campaignInfo.status as StatusCampaign | StatusSubCampaign, // TODO Missing type on status
  }) || isUserInRecipients(campaignInfo?.recipients, authService.userCode);
  const validateCampaignForm = useCampaignValidate();
  const location = useLocation();
  const { goTo } = useGoTo();
  const { setLoading } = useGlobalSpinnerContext();
  const { isCreatorView } = useFieldSelection({});
  const isVanCampaignRole = isVANCR && authService?.checkRole(VAN_CAMPAIGN_MANAGER);
  const campaignInfoVersion = campaignInfo?.version;
  const isInCreation = campaignInfo?.status === statusCampaign.IN_CREATION;
  const vanDisplayVisibility = () => (isInCreation && authService?.checkRoles([...VAN_ROLES]) && campaignInfoVersion > 1);
  const fetchCampaign = useApiRequest(campaignServices.getCampaign);

  const handleReloadCR = (fullReset?: boolean, crVersion?: number) => {
    loadCRData(fullReset, crVersion?.toString());
  };
  const checkIsSubCampaignRecipients = () => campaignServices.checkSubCampaignRecipients(id).then((res) => setIsSubCRUserAssigned(res))
    .catch((err) => {
      addToast(err.message, 'ERROR');
    });

  useEffect(() => {
    const fetchData = async () => {
      if (campaignSearch?.selectedCoincidenceFieldId) {
        await new Promise((resolve) => setInterval(() => { // wait until the field is rendered
          if (document.getElementById('campaignDetailContent')) resolve(1);
        }, 200));
        scrollToField('campaignDetailContent', campaignSearch?.selectedCoincidenceFieldId);
      }
    };
    fetchData();
  }, [campaignSearch?.selectedCoincidenceFieldId]);

  useEffect(() => {
    if (isVanCampaignRole) {
      checkIsSubCampaignRecipients();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCRData = (fullReset = false, crVersion = version) => {
    fetchCampaign(id, '', crVersion)
      .then(async (crInfo) => {
        if (crInfo.activeLanguages?.length === 1) {
          setHeaderLang(crInfo.defaultLanguage);
        }

        setInfo(crInfo, fullReset);
      })
      .catch((err) => {
        if (err.name !== errorTypes.ABORT_ERROR) {
          setInfo({ id: id || '' }, fullReset);
          addToast(err.message, 'ERROR');

          if (err.status === 404) goTo(ROUTES.DASHBOARD, 2500);
        }
      })
      .finally(() => setLoading(false));

    if (searchCR) {
      (campaignServices.getHighligtsOnSearch(id, true, searchCR))
        .then((res) => {
          const crInfo = res;
          const crSearch: CampaignSearch = getCRSearchFromCRInfo(crInfo, searchCR);

          goTo({
            pathname: `/campaign/${id}${getVersionUrlParam(version)}/${tabsCRSearchOrdered()[crSearch.fieldSelected?.tab ?? 'description']}`,
            search: location.search,
          });
          setCRSearch(crSearch);
        }).catch((err) => {
          setInfo({ id: id || '' }, fullReset);
          addToast(err.message, 'ERROR');
        })
        .finally(() => setLoading(false));
    }
  };

  useEffect(() => {
    loadCRData();

    return () => {
      resetCR();
      toggleHistory(false);
    };
  }, [id, searchCR]);// eslint-disable-line react-hooks/exhaustive-deps

  const onLoadSummary = async () => {
    if (isSummary && campaignInfo?.id && !readOnly && !validationsActivated?.all) {
      setIsValid(false);
      const isCampaingFormValid = await validateCampaignForm();

      if (!isCampaingFormValid) {
        goTo(`${ROUTES.CR_DETAIL}/${id}${getVersionUrlParam(version)}/description`);
      }
    }

    setIsValid(true);
  };

  const defineTab = (table: string) => (table === 'plant-feedback' ? 'summary_feedback' : tabLowerCase(tab)?.replaceAll('-', '_'));

  const findTab = () => helpTextTabs.find((campaignTab) => campaignTab.link === tabLowerCase(tab));

  const closeSUMSCriteriaPopup = () => goTo(`${ROUTES.CR_DETAIL}/${id}/description`);

  useEffect(() => {
    const TABS_WITHOUT_HELP_TEXT = ['plant-feedback', 'vehicle-status-overview']; // TODO: help texts in plan feedback and Vehicle Status Overview?

    if ((tab === undefined || !findTab()) && campaignInfo?.status && (tab && !TABS_WITHOUT_HELP_TEXT.includes(tab))) {
      goTo(`${ROUTES.CR_DETAIL}/${id}${getVersionUrlParam(version)}/${isInCreation ? 'description' : 'summary'}`);
    }
    onLoadSummary();
  }, [campaignInfo?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    titleService.updatePageTitle(t('campaignRequests.campaign'), id);
  }, [t, id, lang]);

  useEffect(() => {
    const fetchHighlightedFields = async () => {
      setLoading(true);
      try {
        const highlightedFieldsResponse = await campaignServices.getSelectedFields(id, campaignInfoVersion);
        const selectedFields: SelectedFieldsByTab = highlightedFieldsResponse?.selectedFields || {};
        const hasSelectedFields = Object.values(selectedFields).some((fields) => fields?.length > 0);

        if (!hasSelectedFields) return;

        setSelectedFields({
          ...highlightedFieldsInitialState,
          selectedFields,
        });
        addToast(t('alert.highlightedFieldsInfo'), 'Info');
      } catch (error) {
        addToast(error instanceof Error ? error.message : t('errors.errorBoundaryMessage1'), 'ERROR');
      } finally {
        setLoading(false);
      }
    };

    if (!isCreatorView) {
      setSelectedFields(highlightedFieldsInitialState);

      return;
    }

    fetchHighlightedFields();
  }, [isCreatorView]); // eslint-disable-line react-hooks/exhaustive-deps

  const inlineStyles = {
    gridTemplateColumns: `${resizerWF}px auto`,
    ...(campaignHelp?.show ? { width: `calc(100% - ${resizerHelp}px)` } : {}),
  };

  const getTabComponent = (currentTab, version) => {
    const components = {
      attachments: !version
        ? <Attachments reloadCR={loadCRData} />
        : <Attachments />,
      classification: <Classification />,
      'affected-hardware': <Hardware subCR={isSubCRUserAssigned} />,
      'affected-software': <Software subCR={isSubCRUserAssigned} />,
      'affected-vehicles': <AffectedVehicles />,
      recipients: <Recipients subCR={isSubCRUserAssigned} />,
      translations: <Translations />,
      summary: <Summary />,
      'summary-translations': <Translations />,
      'plant-feedback': <PlantFeedback />,
      'vehicle-status-overview': <VehicleStatusOverview />,
      description: <Description subCR={isSubCRUserAssigned} />,
    };

    return components[currentTab] ?? components.description;
  };

  return (
    <>
      <div className={campaignHelp?.show ? styles.BigContainerFlex : styles.BigContainer}>
        <div className={styles.CampaignDetail} style={inlineStyles}>
          {isLoading && <CampaignStatusSkeleton />}
          {!isLoading && (
            <CampaignStatus
              isSubCR={isSubCRUserAssigned}
              subCRStatus={campaignInfo?.status ?? ''}
            />
          )}
          <div className={styles.DetailContent}>
            {isLoading && (
              <>
                <CampaignHeaderSkeleton isSummary={isSummary} />
                <Spinner isVisible isMask />
              </>
            )}
            {!isLoading && (
              <>
                <CampaignHeader isLoading={Boolean(isLoading)} campaignHelp={campaignHelp} reloadCR={loadCRData} />
                {!vanDisplayVisibility && !canSeeCR(VisibilityMatrixAction.CAMPAIGN_TABS)
                  ? <div className={styles.Permissions}><h2>{t('campaignRequests.permissions')}</h2></div>
                  : (
                    <div className={styles.PageContent}>
                      {campaignHistory?.show && <CampaignHistory id={id || ''} reloadCR={handleReloadCR} currentTab={tab || ''} />}
                      {validationsActivated?.all && <ScrollValidationIndicator containerId="campaign_detail_content" headerId="campaign_header" validations={validations} />}
                      <div id="campaign_detail_content" className={styles.TabContent}>
                        {getTabComponent(tab, version)}
                      </div>
                    </div>
                  )}
              </>
            )}
          </div>
        </div>
        {campaignHelp?.show && findTab() && <Help tab={defineTab(tab || '')} />}
      </div>
      {campaignSearch?.active && <CampaignSearchPopup />}

      <SumsCriteriaPopup
        close={closeSUMSCriteriaPopup}
        open={showSUMSPopup === '1'}
      />
    </>
  );
};

const mapStateToProps = (state) => ({
  campaignInfo: getCampaignInfo(state),
  campaignHelp: getCampaignHelp(state),
  campaignHistory: getCampaignHistory(state),
  resizerWF: getWFResizer(state),
  resizerHelp: getHelpResizer(state),
  readOnly: getCampaignInfo(state)?.authorization?.readOnly,
  validationsActivated: getCampaignValidationsActive(state),
  campaignSearch: getCampaignSearch(state),
  validations: getCampaignValidations(state),
  isVANCR: getIsVANCR(state),
});

const actionCreators = {
  setInfo: setCampaignInfo,
  setHeaderLang: setHeaderLanguage,
  resetCR: resetCampaign,
  setCRSearch: setCampaignSearch,
  setCampaignTranslations: setTranslations,
  toggleHistory: toggleCampaignHistory,
  setSelectedFields: setHighlightedFields,
};

export default connect(mapStateToProps, actionCreators)(CampaignDetail);
